import React, { useState, useEffect, useContext } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { useThemeToggle } from '../../theme';

const ListDiveSiteStubs = () => {
  const { currentUser } = useAuth();
  const { theme } = useThemeToggle(); 

  const [claimedStubs, setClaimedStubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [withdrawing, setWithdrawing] = useState(null); // Track which stub is being withdrawn
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
  const [stubToWithdraw, setStubToWithdraw] = useState(null);

  useEffect(() => {
    const fetchClaimedStubs = async () => {
      if (!currentUser) {
        setError("You must be logged in to see your claimed divesite bounties.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');
        const token = await currentUser.getIdToken();
        const response = await fetch('/api/divesitestubs/my-claimed', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || `Failed to fetch claimed bounties. (Status: ${response.status})`);
        }

        const data = await response.json();
        setClaimedStubs(data);
      } catch (err) {
        console.error("Error fetching claimed bounties:", err);
        setError(err.message || "An unexpected error occurred.");
      } finally {
        setLoading(false);
      }
    };

    fetchClaimedStubs();
  }, [currentUser]);

  // Handle withdrawing from review
  const handleWithdrawFromReview = async (stubId) => {
    setWithdrawing(stubId);
    setError('');
    setSuccess('');

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`/api/divesitestubs/${stubId}/withdraw`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to withdraw from review');
      }

      const updatedStub = await response.json();
      
      // Update the stub in the local state
      setClaimedStubs(prev => 
        prev.map(stub => 
          stub._id === stubId ? updatedStub : stub
        )
      );
      
      setSuccess('Bounty withdrawn from review successfully! You can now edit it.');
      setWithdrawDialogOpen(false);
      setStubToWithdraw(null);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setWithdrawing(null);
    }
  };

  // Helper function to get status display info
  const getStatusInfo = (status) => {
    switch (status) {
      case 'claimed':
        return { color: 'primary', label: 'Claimed' };
      case 'underreview':
        return { color: 'warning', label: 'Under Review' };
      case 'approved':
        return { color: 'success', label: 'Approved' };
      case 'rejected':
        return { color: 'error', label: 'Rejected' };
      default:
        return { color: 'default', label: status };
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3, color: theme.palette.text.primary }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading your claimed divesite bounties...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }} onClose={() => setError('')}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {success && (
        <Alert severity="success" sx={{ m: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      
      <Card sx={{ mt: 3, mb: 2, backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary }}>
        <CardContent>
          <Typography variant="h6" gutterBottom color="inherit">
            Your Dive Site Bounties
          </Typography>
          {claimedStubs.length === 0 ? (
            <Typography variant="body1" color={theme.palette.text.secondary}>
              You have not claimed any divesite bounties yet.
            </Typography>
          ) : (
            <List disablePadding>
              {claimedStubs.map((stub, index) => {
                const statusInfo = getStatusInfo(stub.stubInfo?.status);
                const status = stub.stubInfo?.status;
                
                return (
                  <React.Fragment key={stub._id}>
                    <ListItem alignItems="flex-start" sx={{ py: 2 }}>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle1" component="span" color="inherit">
                            {stub.uddf?.divesite?.site?.name || "Unnamed Stub"}
                          </Typography>
                        }
                        secondary={
                          <>
                            <Typography
                              component="span"
                              variant="body2"
                              color={theme.palette.text.secondary}
                              sx={{ display: 'block', mb: 0.5 }}
                            >
                              Claimed on: {new Date(stub.stubInfo.claimedDate).toLocaleDateString()}
                            </Typography>
                            <Chip
                              label={statusInfo.label}
                              size="small"
                              color={statusInfo.color}
                              sx={{ mr: 1, mb: 0.5 }}
                            />
                            {status === 'claimed' && stub.stubInfo.rejectionReason && (
                              <Typography variant="caption" color="error" sx={{ display: 'block', fontStyle: 'italic' }}>
                                Previous rejection: {stub.stubInfo.rejectionReason}
                              </Typography>
                            )}
                            {status === 'approved' && (
                              <Typography variant="caption" color="success.main" sx={{ display: 'block', fontStyle: 'italic' }}>
                                Published and visible to all users
                              </Typography>
                            )}
                            {status === 'underreview' && (
                              <Typography variant="caption" color="warning.main" sx={{ display: 'block', fontStyle: 'italic' }}>
                                Being reviewed by administrators
                              </Typography>
                            )}
                          </>
                        }
                      />
                      <CardActions sx={{ p: 0, flexDirection: 'column', gap: 1 }}>
                        {/* Primary action button */}
                        {status === 'claimed' && (
                          <Button
                            component={RouterLink}
                            to={`/stubs/edit/${stub._id}`}
                            size="small"
                            variant="contained"
                            color="primary"
                          >
                            Edit
                          </Button>
                        )}
                        
                        {status === 'underreview' && (
                          <>
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              disabled
                            >
                              Under Review
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="secondary"
                              onClick={() => {
                                setStubToWithdraw(stub);
                                setWithdrawDialogOpen(true);
                              }}
                              disabled={withdrawing === stub._id}
                            >
                              {withdrawing === stub._id ? 'Withdrawing...' : 'Withdraw'}
                            </Button>
                          </>
                        )}
                        
                        {status === 'approved' && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            disabled
                          >
                            Published
                          </Button>
                        )}
                      </CardActions>
                    </ListItem>
                    {index < claimedStubs.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Withdraw Confirmation Dialog */}
      <Dialog
        open={withdrawDialogOpen}
        onClose={() => setWithdrawDialogOpen(false)}
      >
        <DialogTitle>Withdraw from Review</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to withdraw "{stubToWithdraw?.uddf?.divesite?.site?.name || 'this divesite bounty'}" from admin review? 
            <br /><br />
            This will remove it from the admin review queue and allow you to edit it again. You can resubmit it for review later.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWithdrawDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={() => handleWithdrawFromReview(stubToWithdraw._id)} 
            color="secondary" 
            variant="contained"
            disabled={withdrawing}
          >
            {withdrawing ? 'Withdrawing...' : 'Withdraw'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ListDiveSiteStubs;
